import { useNavigate } from '@tanstack/react-router';
import { CircleCheck, CircleX, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '~/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '~/components/ui/popover';
import { useTaxonomyStudioStore } from '~/store/taxonomy-studio.store';
import { cn } from '~/utils/cn';

import type { CheckIssue, DraftChecks } from '../../types/taxonomy-studio.types';
import { issueDestination } from '../../utils/check-target';
import { QueryError } from '../shared/query-error';
import { TaxPill } from '../shared/tax-pill';

/** "Ready to publish" / "n issues" badge with a popover listing every check and a Go to link. */
export function ChecksPopover({
  checks,
  isError = false,
  error,
  onRetry,
}: {
  checks: DraftChecks | undefined;
  isError?: boolean;
  error?: unknown;
  onRetry?: () => void;
}) {
  const { t } = useTranslation('taxonomy-studio');
  const navigate = useNavigate();
  // Controlled through the store so a publish rejected with CHECKS_FAILED can open it.
  const open = useTaxonomyStudioStore((s) => s.checksOpen);
  const setOpen = useTaxonomyStudioStore((s) => s.setChecksOpen);
  if (isError) {
    return (
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className="text-error h-8 cursor-pointer rounded-full"
          >
            <CircleX />
            {t('checks.unavailable')}
          </Button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-[26rem] p-3">
          <QueryError error={error} onRetry={onRetry} compact />
        </PopoverContent>
      </Popover>
    );
  }
  if (!checks) {
    return (
      <span className="text-muted-foreground inline-flex h-8 items-center gap-1.5 text-xs">
        <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
        {t('checks.loading')}
      </span>
    );
  }
  const errorCount = checks.errors.length;
  const ready = checks.ready && errorCount === 0;

  const goTo = (issue: CheckIssue) => {
    const dest = issueDestination(issue.target);
    if (!dest) return;
    setOpen(false);
    if (dest.kind === 'node') {
      void navigate({ to: '/taxonomy/products', search: { node: dest.id } });
    } else {
      void navigate({ to: '/taxonomy/attributes', search: { section: dest.key } });
    }
  };

  const groups: { level: 'error' | 'warning' | 'info'; items: CheckIssue[] }[] = [
    { level: 'error', items: checks.errors },
    { level: 'warning', items: checks.warnings },
    { level: 'info', items: checks.info },
  ];

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={cn(
            'h-8 cursor-pointer rounded-full',
            ready ? 'text-success border-success/40' : 'text-error border-error/40',
          )}
        >
          {ready ? <CircleCheck /> : <CircleX />}
          {ready ? t('checks.ready') : t('checks.issues', { count: errorCount })}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[26rem] p-0">
        <div className="border-border border-b px-4 py-3">
          <p className="text-foreground text-sm font-medium">{t('checks.title')}</p>
          <p className="text-muted-foreground text-xs">{t('checks.subtitle')}</p>
        </div>
        <div className="max-h-80 overflow-y-auto">
          {errorCount === 0 && (
            <p className="text-success px-4 py-3 text-sm">{t('checks.noErrors')}</p>
          )}
          {groups.flatMap((g) =>
            g.items.map((issue, i) => (
              <div
                key={`${g.level}-${issue.code}-${String(i)}`}
                className="border-border flex items-start gap-2 border-b px-4 py-2.5 last:border-b-0"
              >
                <TaxPill
                  tone={g.level === 'error' ? 'error' : g.level === 'warning' ? 'warning' : 'info'}
                >
                  {t(`checks.level.${g.level}`)}
                </TaxPill>
                <span className="text-foreground min-w-0 flex-1 text-sm">{issue.message}</span>
                {issueDestination(issue.target) && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 cursor-pointer"
                    onClick={() => {
                      goTo(issue);
                    }}
                  >
                    {t('checks.goTo')}
                  </Button>
                )}
              </div>
            )),
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
